/**
 * @author       hugo@zumper.com (Hugo Pineda)
 * @copyright    Copyright (c) 2020, Zumper
 */

import type { BuildingAmenity, ListingAmenity } from '@zcode/enums'
import { checkCats, checkDogs } from '@zcode/nr/es/shared/bizos/rentable_utils'
import { displayAmenity, displayBuildingAmenity } from '@zcode/nr/es/shared/utils/amenity'
import type { Listing, MinPAListingData } from '@zcode/state-type'

export interface PAAmenities {
  buildingAmenities: string[]
  catsOk: boolean | null
  dogsOk: boolean | null
  otherAmenities: string[]
}

export const getPAMajorityPets = (listings: ReadonlyArray<Listing<MinPAListingData>>) => {
  let catsOkCount = 0
  let dogsOkCount = 0
  let noCatsCount = 0
  let noDogsCount = 0
  for (const listing of listings) {
    if (listing.pets) {
      if (checkCats(listing.pets)) {
        catsOkCount++
      } else {
        noCatsCount++
      }
      if (checkDogs(listing.pets)) {
        dogsOkCount++
      } else {
        noDogsCount++
      }
    }
  }
  return {
    catsOk:
      catsOkCount >= listings.length / 2 || (noCatsCount >= listings.length / 2 ? false : null),
    dogsOk:
      dogsOkCount >= listings.length / 2 || (noDogsCount >= listings.length / 2 ? false : null),
  }
}

export const getPAAmenities = (listings?: ReadonlyArray<Listing<MinPAListingData>> | null) => {
  const buildingAmenities: string[] = []
  const otherAmenities: string[] = []
  const paAmenities: PAAmenities = {
    buildingAmenities,
    catsOk: null,
    dogsOk: null,
    otherAmenities,
  }

  if (!listings?.length) {
    return paAmenities
  }

  const initialListingAmenities = new Set<ListingAmenity>()
  const initialBuildingAmenities = new Set<BuildingAmenity>()

  for (const listing of listings) {
    listing.amenities?.forEach((amenity) => {
      initialListingAmenities.add(amenity)
    })
    listing.buildingAmenities?.forEach((amenity) => {
      initialBuildingAmenities.add(amenity)
    })
  }

  paAmenities.dogsOk = listings.some((listing) => checkDogs(listing.pets))
  paAmenities.catsOk = listings.some((listing) => checkCats(listing.pets))

  if (paAmenities.dogsOk) {
    buildingAmenities.push('Dogs Ok')
  }

  if (paAmenities.catsOk) {
    buildingAmenities.push('Cats Ok')
  }

  for (const key of initialBuildingAmenities) {
    buildingAmenities.push(displayBuildingAmenity(key))
  }

  for (const key of initialListingAmenities) {
    otherAmenities.push(displayAmenity(key))
  }

  return paAmenities
}
